/**
 * A label's words as runs (`TextRun`): laid out in lines — wrapped at the width the box is given, a
 * word never broken (not even where a look changes inside it), a '\n' a line of its own — and drawn,
 * each stretch in its own look. Plain words are one run in the label's own type: laid out and drawn
 * exactly as a line of plain words always was.
 */
import type { BoxLook, TextRun } from '../../shared/doc.ts';
import { FAIL, fontString, INK, PASS } from './style.ts';
import type { Measurer } from './text.ts';

/** The type a label sets its words in (its own runs may be bolder, italic, another colour). */
export interface TextBase {
  weight: number;
  size: number;
  color: string;
}
/** One stretch of a line in one look, as drawn: from `x` (from the line's start), `w` wide. */
export interface Piece {
  t: string;
  x: number;
  w: number;
  /** Its width less the spaces it ends with: what is underlined or struck through. */
  ink: number;
  weight: number;
  italic: boolean;
  under: boolean;
  strike: boolean;
  /** Its own colour, or null: the label's. */
  color: string | null;
}
export interface RichLine {
  pieces: Piece[];
  w: number;
}
/** Words laid out: their lines, and the widest of them. */
export interface RichText {
  lines: RichLine[];
  w: number;
  size: number;
}

/** A bold stretch: a step past the boldest a label is set in (a badge's name, a tag's words are 700). */
const BOLD = 800;

/**
 * The colours a stretch of words may take (2026-10-28): the map's own — ink, the failing red and the
 * passing green, the tags' blue — and a few more that read on white at a report's size.
 */
export const TEXT_COLORS: readonly { key: string; name: string; color: string }[] = [
  { key: 'ink', name: 'Black', color: INK },
  { key: 'red', name: 'Red', color: FAIL },
  { key: 'green', name: 'Green', color: PASS },
  { key: 'blue', name: 'Blue', color: '#005EFF' },
  { key: 'orange', name: 'Orange', color: '#B35A06' },
  { key: 'purple', name: 'Purple', color: '#6236B5' },
  { key: 'teal', name: 'Teal', color: '#0E7C78' },
];
/** A stretch's colour as drawn: a key of `TEXT_COLORS`, or a colour of one's own. */
export function textColor(c: string | undefined): string | null {
  if (!c) return null;
  if (/^#[0-9a-f]{6}$/i.test(c)) return c;
  return TEXT_COLORS.find((x) => x.key === c)?.color ?? null;
}

/** The words of plain text, as runs. */
export function runsOf(text: string): TextRun[] {
  return text ? [{ t: text }] : [];
}

interface Atom {
  /** 'w' a stretch of a word (a word may be several, where its look changes), 's' spaces, 'n' a line break. */
  k: 'w' | 's' | 'n';
  t: string;
  run: TextRun;
}
function atomsOf(runs: readonly TextRun[]): Atom[] {
  const out: Atom[] = [];
  for (const run of runs) {
    for (const part of run.t.split(/(\n| +)/)) {
      if (!part) continue;
      out.push({ k: part === '\n' ? 'n' : part.startsWith(' ') ? 's' : 'w', t: part, run });
    }
  }
  return out;
}
const weightOf = (run: TextRun, base: TextBase): number => (run.b ? Math.max(BOLD, base.weight) : base.weight);
const widthOf = (t: string, run: TextRun, base: TextBase, m: Measurer): number => m.width(t, weightOf(run, base), base.size, !!run.i);

/** The words of a run list cut into what wraps as one: whole words (each its stretches), the spaces between them, the line breaks. */
interface Token {
  k: 'w' | 's' | 'n';
  atoms: Atom[];
  w: number;
}
function tokensOf(runs: readonly TextRun[], base: TextBase, m: Measurer): Token[] {
  const out: Token[] = [];
  for (const a of atomsOf(runs)) {
    const last = out[out.length - 1];
    const w = a.k === 'n' ? 0 : widthOf(a.t, a.run, base, m);
    // (a word's stretches are one word, and the spaces between two words one gap, whatever runs they are of)
    if ((a.k === 'w' || a.k === 's') && last?.k === a.k) {
      last.atoms.push(a);
      last.w += w;
    } else out.push({ k: a.k, atoms: [a], w });
  }
  return out;
}

/**
 * Words laid out in lines no wider than `maxW` (null: a line per line break, however long): a word
 * that will not fit after the last on its line begins the next; one wider than the whole width is
 * left whole on a line of its own (a box is never made narrower than its widest word: `widestWord`).
 * The spaces where a line wraps are not drawn. Each line's stretches of one look are one piece,
 * measured whole.
 */
export function layoutRich(runs: readonly TextRun[], base: TextBase, maxW: number | null, m: Measurer): RichText {
  const lines: Atom[][] = [[]];
  let pending: Token | null = null; // the spaces before the next word
  for (const tok of tokensOf(runs, base, m)) {
    const line = lines[lines.length - 1]!;
    if (tok.k === 'n') {
      lines.push([]);
      pending = null;
      continue;
    }
    if (tok.k === 's') {
      // (spaces at a line's start are kept, as typed; between words they wait for the next word)
      if (!line.length) line.push(...tok.atoms);
      else pending = tok;
      continue;
    }
    // (whether the word fits is asked of the line as it would be drawn — measured just as a line's width
    // is, below — never of its words and spaces added up, which kerning and rounding make a hair
    // different: a box as wide as its lines must wrap them just so again)
    const next = [...line, ...(pending?.atoms ?? []), ...tok.atoms];
    if (maxW !== null && line.length && lineOf(next, base, m).w > maxW + 0.01) lines.push([...tok.atoms]);
    else line.splice(0, line.length, ...next);
    pending = null;
  }
  const out = lines.map((atoms) => lineOf(atoms, base, m));
  return { lines: out, w: Math.max(0, ...out.map((l) => l.w)), size: base.size };
}
/** A line's atoms drawn: its stretches of one look each a piece, measured whole; as wide as its words, not the spaces it may end with. */
function lineOf(atoms: readonly Atom[], base: TextBase, m: Measurer): RichLine {
  const pieces: Piece[] = [];
  let x = 0;
  const flush = (t: string, run: TextRun) => {
    const w = widthOf(t, run, base, m);
    const bare = t.trimEnd();
    pieces.push({ t, x, w, ink: bare === t ? w : widthOf(bare, run, base, m), weight: weightOf(run, base), italic: !!run.i, under: !!run.u, strike: !!run.s, color: textColor(run.c) });
    x += w;
  };
  let t = '';
  let cur: TextRun | null = null;
  for (const a of atoms) {
    if (cur && !same(cur, a.run)) {
      flush(t, cur);
      t = '';
      cur = null;
    }
    cur ??= a.run;
    t += a.t;
  }
  if (cur && t) flush(t, cur);
  const last = pieces[pieces.length - 1];
  return { pieces, w: last ? last.x + last.ink : 0 };
}
const same = (a: TextRun, b: TextRun) => !!a.b === !!b.b && !!a.i === !!b.i && !!a.u === !!b.u && !!a.s === !!b.s && (a.c ?? '') === (b.c ?? '');

/** The widest word of a run list: as narrow as its box's words may be wrapped. */
export function widestWord(runs: readonly TextRun[], base: TextBase, m: Measurer): number {
  let w = 0;
  for (const tok of tokensOf(runs, base, m)) if (tok.k === 'w') w = Math.max(w, tok.w);
  return w;
}

/** Where a box's lines sit in it. */
export type Align = 'left' | 'center' | 'right';
export function alignOf(look: BoxLook | undefined): Align {
  return look?.align ?? 'left';
}

/**
 * Laid-out words drawn: the first line's middle `lineH / 2` below `top`, each line `lineH` below the
 * last, set within `innerW` from `left` as `align` says; each piece in its own look (its colour, or
 * `color`). An underline sits just under the words, a strike through their middle, each as heavy as
 * the type wants.
 */
export function drawRich(ctx: CanvasRenderingContext2D, text: RichText, left: number, top: number, lineH: number, innerW: number, align: Align, color: string): void {
  const size = text.size;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  text.lines.forEach((line, i) => {
    const x = left + (align === 'center' ? (innerW - line.w) / 2 : align === 'right' ? innerW - line.w : 0);
    const y = top + lineH * i + lineH / 2 + size * 0.36;
    for (const p of line.pieces) {
      ctx.font = fontString(p.weight, size, p.italic);
      ctx.fillStyle = p.color ?? color;
      ctx.fillText(p.t, x + p.x, y);
      if (p.under || p.strike) {
        const th = Math.max(size * 0.065, 0.5);
        // (not under the spaces a stretch ends with)
        if (p.under) ctx.fillRect(x + p.x, y + size * 0.13, p.ink, th);
        if (p.strike) ctx.fillRect(x + p.x, y - size * 0.3, p.ink, th);
      }
    }
  });
}
